import { z } from 'zod';

import { AvaAIToolExecution } from '../toolExecution.mts';

export const AvaAIOrderSide = {
  Buy: 'buy',
  Sell: 'sell',
} as const;
export type AvaAIOrderSide = (typeof AvaAIOrderSide)[keyof typeof AvaAIOrderSide];

const positiveDecimalString = z
  .string()
  .max(100)
  .regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/, 'Quantity must be a plain decimal string.')
  .refine(value => /[1-9]/.test(value), 'Quantity must be greater than zero.');

/** Arguments accepted by the client-side order-form priming tool. */
export const primeOrderFormInputSchema = z.strictObject({
  quantity: positiveDecimalString,
  side: z.enum([AvaAIOrderSide.Buy, AvaAIOrderSide.Sell]),
  symbol: z
    .string()
    .min(1)
    .max(100)
    .refine(symbol => symbol.trim() === symbol, 'Symbol must not contain whitespace.'),
});
export type PrimeOrderFormInput = z.infer<typeof primeOrderFormInputSchema>;

export const AvaAIPrimeOrderFormErrorCode = {
  InvalidInput: 'INVALID_INPUT',
  SymbolNotAvailable: 'SYMBOL_NOT_AVAILABLE',
  UnsupportedProduct: 'UNSUPPORTED_PRODUCT',
  MultiplePrimesNotSupported: 'MULTIPLE_PRIMES_NOT_SUPPORTED',
  PrimeFailed: 'PRIME_FAILED',
} as const;
export type AvaAIPrimeOrderFormErrorCode =
  (typeof AvaAIPrimeOrderFormErrorCode)[keyof typeof AvaAIPrimeOrderFormErrorCode];

const primeOrderFormErrorCodeSchema = z.enum([
  AvaAIPrimeOrderFormErrorCode.InvalidInput,
  AvaAIPrimeOrderFormErrorCode.SymbolNotAvailable,
  AvaAIPrimeOrderFormErrorCode.UnsupportedProduct,
  AvaAIPrimeOrderFormErrorCode.MultiplePrimesNotSupported,
  AvaAIPrimeOrderFormErrorCode.PrimeFailed,
]);

const primeOrderFormSuccessSchema = z.strictObject({
  order: primeOrderFormInputSchema,
  status: z.literal('success'),
});

const primeOrderFormErrorSchema = z.strictObject({
  code: primeOrderFormErrorCodeSchema,
  message: z.string().min(1).max(500),
  status: z.literal('error'),
});

/** Result sent by Ava after attempting to apply a draft to the order form. */
export const primeOrderFormResultSchema = z.discriminatedUnion('status', [
  primeOrderFormSuccessSchema,
  primeOrderFormErrorSchema,
]);
export type PrimeOrderFormResult = z.infer<typeof primeOrderFormResultSchema>;

export const primeOrderFormToolContract = {
  execution: AvaAIToolExecution.Client,
  name: 'primeOrderForm',
  inputSchema: primeOrderFormInputSchema,
  outputSchema: primeOrderFormResultSchema,
} as const;
